import React, { useState, useEffect } from "react";
import {Search, SortAsc, SortDesc, Download, ChevronLeft, ChevronRight, Trash2,} from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "../../auth/AuthProvider";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const StudentList = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const { t } = useTranslation("studentlist");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [classes, setClasses] = useState([]);
  const [classMap, setClassMap] = useState({});
  const [streamMap, setStreamMap] = useState({});

  // States for filtering and sorting
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedStream, setSelectedStream] = useState("");
  const [availableStreams, setAvailableStreams] = useState([]);

  // Pagination from server
  const [totalStudents, setTotalStudents] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 50; // Match backend page size

  useEffect(() => {
    const fetchClassesAndStreams = async () => {
      try {
        const response = await api.get(
          `/api/v1/admin/schools/${user.school_reg}/classes`
        );
        setClasses(response.data);

        // Create mapping of IDs to names
        const classMapping = {};
        const streamMapping = {};

        response.data.forEach((cls) => {
          classMapping[cls.id] = cls.name;
          cls.streams.forEach((stream) => {
            streamMapping[stream.id] = stream.name;
          });
        });

        setClassMap(classMapping);
        setStreamMap(streamMapping);
      } catch (err) {
        setError(err.message);
      }
    };

    fetchClassesAndStreams();
  }, [api, user.school_reg]);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);

        // Build query parameters for filtering
        const params = new URLSearchParams();
        params.append("page", currentPage);
        params.append("page_size", pageSize);

        if (searchTerm) {
          params.append("search", searchTerm);
        }

        if (selectedClass) {
          params.append("class_id", selectedClass);
        }

        if (selectedStream) {
          params.append("stream_id", selectedStream);
        }

        if (sortField) {
          const sortParam = `${
            sortDirection === "desc" ? "-" : ""
          }${sortField}`;
          params.append("sort", sortParam);
        }

        const response = await api.get(
          `/api/v1/admin/schools/${
            user.school_reg
          }/students?${params.toString()}`
        );

        if (response.data.items) {
          setStudents(response.data.items);
          setTotalStudents(response.data.total);
          setTotalPages(response.data.total_pages);
        } else {
          // Fallback for different API response format
          setStudents(response.data);
          setTotalStudents(response.data.length);
          setTotalPages(Math.ceil(response.data.length / pageSize));
        }

        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
    // Reset selections when parameters change
    setSelectedStudents([]);
    setSelectAll(false);
  }, [
    api,
    user.school_reg,
    currentPage,
    searchTerm,
    selectedClass,
    selectedStream,
    sortField,
    sortDirection,
  ]);

  // Update available streams when selected class changes
  useEffect(() => {
    if (selectedClass) {
      const selectedClassData = classes.find(
        (c) => c.id.toString() === selectedClass
      );
      setAvailableStreams(selectedClassData?.streams || []);
      setSelectedStream(""); // Reset stream selection when class changes
    } else {
      setAvailableStreams([]);
    }
  }, [selectedClass, classes]);

  const handleRowClick = (studentId, e) => {
    // Prevent navigation if the click was on a checkbox
    if (e.target.type === "checkbox" || e.target.closest(".checkbox-cell")) {
      return;
    }
    navigate(`/student-profile/${studentId}`);
  };

  const handleCheckboxChange = (studentId) => {
    setSelectedStudents((prev) => {
      if (prev.includes(studentId)) {
        return prev.filter((id) => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map((student) => student.id));
    }
    setSelectAll(!selectAll);
  };

  const handleDeleteSelected = async () => {
    if (selectedStudents.length === 0) return;

    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      // Use Promise.all to handle multiple delete requests concurrently
      await Promise.all(
        selectedStudents.map((studentId) =>
          api.delete(
            `/api/v1/admin/schools/${user.school_reg}/students/${studentId}`
          )
        )
      );

      // Refresh student list after delete
      setCurrentPage(1); // Reset to first page
      setSelectedStudents([]);
      setSelectAll(false);
      setShowDeleteConfirm(false);
    } catch (err) {
      setError(`Failed to delete students: ${err.message}`);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const getRandomColor = (name) => {
    const colors = [
      "#2563eb",
      "#7c3aed",
      "#db2777",
      "#dc2626",
      "#059669",
      "#ca8a04",
      "#0891b2",
      "#4f46e5",
      "#be123c",
      "#15803d",
    ];
    const index =
      name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
      colors.length;
    return colors[index];
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when search changes
  };

  const exportToCSV = async () => {
    try {
      setLoading(true);

      // Fetch all students for export (without pagination)
      const params = new URLSearchParams();
      params.append("page_size", totalStudents); // Request all students
      params.append("page", 1);

      if (searchTerm) {
        params.append("search", searchTerm);
      }

      if (selectedClass) {
        params.append("class_id", selectedClass);
      }

      if (selectedStream) {
        params.append("stream_id", selectedStream);
      }

      const response = await api.get(
        `/api/v1/admin/schools/${user.school_reg}/students?${params.toString()}`
      );

      const exportData = response.data.items || response.data;

      const headers = [
        t("table.admNo"),
        t("table.name"),
        t("table.gender"),
        t("table.class"),
        t("table.stream"),
        t("table.dateOfJoin"),
        t("table.dateOfBirth"),
        t("table.address"),
      ];

      const csvContent = [
        headers.join(","),
        ...exportData.map((student) =>
          [
            student.admission_number,
            student.name.replace(/,/g, " "), // Replace commas to prevent CSV issues
            student.gender,
            classMap[student.class_id] || student.class_id,
            streamMap[student.stream_id] || student.stream_id,
            student.date_of_joining,
            student.date_of_birth,
            (student.address || "").replace(/,/g, " "), // Replace commas and handle empty addresses
          ].join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "students.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Failed to export students: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderSortIcon = (field) => {
    if (sortField === field) {
      return sortDirection === "asc" ? (
        <SortAsc size={16} className="inline ml-1" />
      ) : (
        <SortDesc size={16} className="inline ml-1" />
      );
    }
    return null;
  };

  const sortableHeaderClasses =
    "bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 cursor-pointer select-none whitespace-nowrap hover:bg-slate-100";

  if (loading && students.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && students.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {t("error", { error })}
      </div>
    );
  }

  return (
    <div className="rounded-xl">
      <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
        {/* Search Bar - Full width on mobile, fixed width on larger screens */}
        <form
          onSubmit={handleSearchSubmit}
          className="w-full md:w-80 bg-white border border-slate-200 rounded-lg px-4 py-2 flex items-center"
        >
          <Search className="text-slate-500 flex-shrink-0" size={20} />
          <input
            type="text"
            placeholder={t("searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-none outline-none ml-2 text-sm w-full"
          />
          <button type="submit" className="hidden">
            Search
          </button>
        </form>

        {/* Filters and Actions Container */}
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
          {/* Filter Group */}
          <div className="flex flex-wrap gap-2">
            <select
              value={selectedClass}
              onChange={(e) => {
                setSelectedClass(e.target.value);
                setCurrentPage(1); // Reset to first page when filter changes
              }}
              className="px-4 py-2 border border-slate-200 rounded-lg bg-white text-sm cursor-pointer flex-1 min-w-[140px]"
            >
              <option value="">{t("allClasses")}</option>
              {classes.map((cls) => (
                <option key={cls.id} value={cls.id}>
                  {cls.name}
                </option>
              ))}
            </select>

            <select
              value={selectedStream}
              onChange={(e) => {
                setSelectedStream(e.target.value);
                setCurrentPage(1); // Reset to first page when filter changes
              }}
              className="px-4 py-2 border border-slate-200 rounded-lg bg-white text-sm cursor-pointer flex-1 min-w-[140px]"
              disabled={!selectedClass}
            >
              <option value="">{t("allStreams")}</option>
              {availableStreams.map((stream) => (
                <option key={stream.id} value={stream.id}>
                  {stream.name}
                </option>
              ))}
            </select>
          </div>

          {/* Action Buttons Group */}
          <div className="flex gap-2 sm:ml-auto">
            {selectedStudents.length > 0 && (
              <button
                className="flex items-center justify-center gap-1 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200 flex-1 sm:flex-none"
                onClick={handleDeleteSelected}
              >
                <Trash2 size={18} />
                <span className="whitespace-nowrap">
                  {t("delete")} ({selectedStudents.length})
                </span>
              </button>
            )}

            <button
              className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200 flex-1 sm:flex-none"
              onClick={exportToCSV}
              disabled={loading}
            >
              <Download size={18} />
              <span>{t("exportCSV")}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-lg border border-slate-200 shadow-sm">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="bg-slate-50 p-3 border-b-2 border-slate-200 whitespace-nowrap w-8">
                <div className="flex items-center justify-center checkbox-cell">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAllChange}
                    className="form-checkbox w-4 h-4 text-blue-500 rounded border-slate-300 cursor-pointer"
                  />
                </div>
              </th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">
                {t("table.photo")}
              </th>
              <th
                className={sortableHeaderClasses}
                onClick={() => {
                  setSortField("admission_number");
                  setSortDirection(
                    sortField === "admission_number"
                      ? sortDirection === "asc"
                        ? "desc"
                        : "asc"
                      : "asc"
                  );
                  setCurrentPage(1); // Reset to first page when sort changes
                }}
              >
                {t("table.admNo")} {renderSortIcon("admission_number")}
              </th>
              <th
                className={sortableHeaderClasses}
                onClick={() => {
                  setSortField("name");
                  setSortDirection(
                    sortField === "name"
                      ? sortDirection === "asc"
                        ? "desc"
                        : "asc"
                      : "asc"
                  );
                  setCurrentPage(1); // Reset to first page when sort changes
                }}
              >
                {t("table.name")} {renderSortIcon("name")}
              </th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">
                {t("table.gender")}
              </th>
              <th
                className={sortableHeaderClasses}
                onClick={() => {
                  setSortField("class_id");
                  setSortDirection(
                    sortField === "class_id"
                      ? sortDirection === "asc"
                        ? "desc"
                        : "asc"
                      : "asc"
                  );
                  setCurrentPage(1); // Reset to first page when sort changes
                }}
              >
                {t("table.class")} {renderSortIcon("class_id")}
              </th>
              <th
                className={sortableHeaderClasses}
                onClick={() => {
                  setSortField("stream_id");
                  setSortDirection(
                    sortField === "stream_id"
                      ? sortDirection === "asc"
                        ? "desc"
                        : "asc"
                      : "asc"
                  );
                  setCurrentPage(1); // Reset to first page when sort changes
                }}
              >
                {t("table.stream")} {renderSortIcon("stream_id")}
              </th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">
                {t("table.dateOfJoin")}
              </th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">
                {t("table.dateOfBirth")}
              </th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">
                {t("table.address")}
              </th>
            </tr>
          </thead>
          <tbody>
            {students.map((student) => (
              <tr
                key={student.id}
                onClick={(e) => handleRowClick(student.id, e)}
                className={`cursor-pointer hover:bg-slate-50 ${
                  selectedStudents.includes(student.id) ? "bg-blue-50" : ""
                }`}
              >
                <td className="p-3 border-b border-slate-200 w-8">
                  <div
                    className="flex items-center justify-center checkbox-cell"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student.id)}
                      onChange={() => handleCheckboxChange(student.id)}
                      className="form-checkbox w-4 h-4 text-blue-500 rounded border-slate-300 cursor-pointer"
                    />
                  </div>
                </td>
                <td className="p-3 border-b border-slate-200">
                  {student.photo ? (
                    <img
                      src={student.photo}
                      alt={student.name}
                      className="w-10 h-10 rounded-full object-cover bg-slate-200"
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white uppercase"
                      style={{ backgroundColor: getRandomColor(student.name) }}
                    >
                      {student.name.charAt(0)}
                    </div>
                  )}
                </td>
                <td className="p-3 border-b border-slate-200 text-slate-700">
                  {student.admission_number}
                </td>
                <td className="p-3 border-b border-slate-200 text-slate-700">
                  {student.name}
                </td>
                <td className="p-3 border-b border-slate-200">
                  <span
                    className={`px-2 py-1 rounded-xl text-xs font-medium ${
                      student.gender.toLowerCase() === "male"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-pink-100 text-pink-800"
                    }`}
                  >
                    {student.gender}
                  </span>
                </td>
                <td className="p-3 border-b border-slate-200 text-slate-700">
                  {classMap[student.class_id] || student.class_id}
                </td>
                <td className="p-3 border-b border-slate-200 text-slate-700">
                  {streamMap[student.stream_id] || student.stream_id}
                </td>
                <td className="p-3 border-b border-slate-200 text-slate-700">
                  {student.date_of_joining
                    ? format(new Date(student.date_of_joining), "dd/MM/yyyy")
                    : "-"}
                </td>
                <td className="p-3 border-b border-slate-200 text-slate-700">
                  {student.date_of_birth
                    ? format(new Date(student.date_of_birth), "dd/MM/yyyy")
                    : "-"}
                </td>
                <td className="p-3 border-b border-slate-200 text-slate-700">
                  {student.address || "-"}
                </td>
              </tr>
            ))}
            {students.length === 0 && !loading && (
              <tr>
                <td colSpan="10" className="p-6 text-center text-slate-500">
                  {t("noStudentsFound")}
                </td>
              </tr>
            )}
            {loading && students.length === 0 && (
              <tr>
                <td colSpan="10" className="p-6 text-center text-slate-500">
                  {t("loading")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Responsive pagination controls */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
        <div className="text-sm text-slate-500 order-2 sm:order-1">
          {t("showing", { shown: students.length, total: totalStudents })}
        </div>

        <div className="flex items-center justify-center gap-4 order-1 sm:order-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1 || loading}
            className="p-2 border border-slate-200 bg-white rounded-lg cursor-pointer flex items-center transition-all duration-200 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="text-slate-600 text-sm">
            {t("page", { current: currentPage, total: totalPages || 1 })}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages || totalPages === 0 || loading}
            className="p-2 border border-slate-200 bg-white rounded-lg cursor-pointer flex items-center transition-all duration-200 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">
              {t("deleteConfirm.title")}
            </h3>
            <p className="mb-6">
              {t("deleteConfirm.message", { count: selectedStudents.length })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                {t("deleteConfirm.cancel")}
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                {t("deleteConfirm.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentList;
